import type { Branding, CompanySettings, MailSettings, MessagingSettings } from '@pm/schemas';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api } from './api';
import { useMe } from './auth';

export const settingsKeys = {
  branding: (orgId: string | undefined) => ['branding', orgId ?? null] as const,
  company: ['company-settings'] as const,
  mail: ['mail-settings'] as const,
  messaging: ['messaging-settings'] as const,
  /** Prefixo para invalidar o branding de qualquer organização. */
  brandingAll: ['branding'] as const,
};

/** Nome de exibição e logotipos da organização atual (Administração → Dados da Empresa). */
export function useBranding() {
  const me = useMe();
  const orgId = me.organization?.id;
  return useQuery({
    queryKey: settingsKeys.branding(orgId),
    queryFn: async () => {
      const b = await api.get<Branding>('/organization/branding');
      rememberBrandOrg('internal', b.organization.id);
      return b;
    },
    enabled: Boolean(orgId),
    staleTime: 5 * 60_000,
  });
}

export function useCompanySettings() {
  return useQuery({
    queryKey: settingsKeys.company,
    queryFn: () => api.get<CompanySettings>('/organization/company'),
  });
}

export function useMessagingSettings() {
  return useQuery({
    queryKey: settingsKeys.messaging,
    queryFn: () => api.get<MessagingSettings>('/organization/settings/messaging'),
  });
}

export function useMailSettings() {
  return useQuery({
    queryKey: settingsKeys.mail,
    queryFn: () => api.get<MailSettings>('/organization/settings/mail'),
  });
}

// ---------- Telas sem sessão (login interno, login e convite do portal) ----------

const REMEMBER_KEYS = { internal: 'pm.brand.org', portal: 'pm.portal.org' } as const;

/** Guarda a organização do último acesso para a tela de login mostrar o logotipo certo. */
export function rememberBrandOrg(area: keyof typeof REMEMBER_KEYS, organizationId: string): void {
  try {
    localStorage.setItem(REMEMBER_KEYS[area], organizationId);
  } catch {
    /* modo privado: sem lembrança, cai no padrão da instalação */
  }
}

export function recalledBrandOrg(area: keyof typeof REMEMBER_KEYS): string | null {
  try {
    return localStorage.getItem(REMEMBER_KEYS[area]);
  } catch {
    return null;
  }
}

/**
 * Identidade visual sem autenticação. A API resolve pelo convite, pela organização lembrada
 * ou, numa instalação com uma só organização, por ela; sem pista confiável devolve null.
 */
export function usePublicBranding(hint: { organization?: string | null; invite?: string | null }) {
  const params = new URLSearchParams();
  if (hint.organization) params.set('organization', hint.organization);
  if (hint.invite) params.set('invite', hint.invite);
  const query = params.toString();
  return useQuery({
    queryKey: ['public-branding', query] as const,
    queryFn: () => api.get<Branding | null>(`/public/branding${query ? `?${query}` : ''}`),
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export function brandInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts.length > 1 ? `${parts[0]![0]}${parts[1]![0]}` : name.slice(0, 2);
  return letters.toUpperCase() || 'PM';
}

/** Há logotipo para exibir? (um só vale para os dois temas) */
export function hasLogo(branding: Branding | null | undefined): boolean {
  return Boolean(branding?.logos.dark ?? branding?.logos.light);
}

/**
 * Favicon da organização na aba do navegador (Dados da Empresa → Favicon). Sem favicon
 * cadastrado, a aba fica com o padrão do navegador.
 */
export function useFavicon(branding: Branding | null | undefined): void {
  const favicon = branding?.logos.favicon;
  useEffect(() => {
    if (!favicon) return;
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.type = favicon.mime_type;
    link.href = favicon.url;
  }, [favicon]);
}
